import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Flex, Heading, Tabs, TabList, TabPanels, Tab, TabPanel,
  Button, Input, Select, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, useToast, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
  FormControl, FormLabel, SimpleGrid, Stat, StatLabel, StatNumber,
  HStack, Spinner, Tag, Menu, MenuButton, MenuList, MenuItem, Code, NumberInput,
  NumberInputField, Text,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon, AddIcon, ChevronDownIcon, CopyIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useShellHeader } from "../../Components/AppShell";
import Pill from "../../Components/ui/Pill";
import LoginHealthModal from "./LoginHealthModal";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function api(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}

const ROLES = ["SUPER_ADMIN", "TEAM_USER"];
const STATUSES = ["active", "suspended", "invited"];

function StatsBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api("/admin/stats").then(setStats).catch(() => {}); }, []);
  if (!stats) return null;
  return (
    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
      <Stat p={4} bg="white" rounded="md" shadow="sm"><StatLabel>Total Users</StatLabel><StatNumber>{stats.totalUsers}</StatNumber></Stat>
      <Stat p={4} bg="white" rounded="md" shadow="sm"><StatLabel>Active</StatLabel><StatNumber color="green.500">{stats.activeUsers}</StatNumber></Stat>
      <Stat p={4} bg="white" rounded="md" shadow="sm"><StatLabel>Suspended</StatLabel><StatNumber color="red.500">{stats.suspendedUsers}</StatNumber></Stat>
      <Stat p={4} bg="white" rounded="md" shadow="sm"><StatLabel>Online (5m)</StatLabel><StatNumber color="blue.500">{stats.onlineUsers}</StatNumber></Stat>
      <Stat p={4} bg="white" rounded="md" shadow="sm"><StatLabel>Teams</StatLabel><StatNumber>{stats.teamCount}</StatNumber></Stat>
    </SimpleGrid>
  );
}

function UsersTab() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const create = useDisclosure();
  const edit = useDisclosure();
  const [editing, setEditing] = useState(null);
  const health = useDisclosure();
  const [healthUser, setHealthUser] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filterTeam) params.set("team", filterTeam);
      if (filterStatus) params.set("status", filterStatus);
      const [u, t] = await Promise.all([
        api(`/admin/users?${params.toString()}`),
        api("/admin/teams"),
      ]);
      setUsers(u.users || []);
      setTeams(t.teams || []);
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function handleDelete(id) {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api(`/admin/users/${id}`, { method: "DELETE" });
      toast({ title: "User deleted", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function handleSuspendToggle(u) {
    const newStatus = u.status === "active" ? "suspended" : "active";
    try {
      await api(`/admin/users/${u._id}`, { method: "PATCH", body: JSON.stringify({ status: newStatus }) });
      toast({ title: `User ${newStatus}`, status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function userAction(u, path, label) {
    if (!window.confirm(`${label} for ${u.email}?`)) return;
    try {
      await api(`/admin/users/${u._id}/${path}`, { method: "POST" });
      toast({ title: `${label} done`, status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  // Reset a user's password to an admin-chosen temporary value. It works at
  // once: accounts don't lock, and the server clears the wrong-try count.
  // Spaces at either end are removed (here and on the server): a pasted
  // "Temp@123 " used to be saved with the space, and the person typing
  // "Temp@123" got "Invalid Credential".
  async function handleResetPassword(u) {
    const raw = window.prompt(`Set a temporary password for ${u.email} (min 6 characters).\nShare it with them and ask them to change it after logging in.`);
    if (raw === null) return; // cancelled
    const pw = raw.trim();
    if (pw.length < 6) { toast({ title: "Password must be at least 6 characters", status: "error" }); return; }
    try {
      await api(`/admin/users/${u._id}/reset-password`, { method: "POST", body: JSON.stringify({ newPassword: pw }) });
      const trimmed = pw !== raw ? " Spaces at the start or end were removed." : "";
      toast({ title: "Password reset", description: `Temp password set for ${u.email}.${trimmed} Share it and ask them to change it after login.`, status: "success", duration: 9000 });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Box>
      <HStack mb={4} spacing={3}>
        <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} maxW="280px" />
        <Select placeholder="All teams" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} maxW="200px">
          {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </Select>
        <Select placeholder="All statuses" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} maxW="180px">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Button onClick={load}>Apply</Button>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={create.onOpen} ml="auto">Add User</Button>
      </HStack>

      {loading ? <Spinner /> : (
        <Box bg="white" rounded="md" shadow="sm" overflow="auto">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Team</Th><Th>Status</Th><Th>Last Seen</Th><Th></Th></Tr>
            </Thead>
            <Tbody>
              {users.map((u) => (
                <Tr key={u._id}>
                  <Td>
                    <Text fontWeight="600">{u.username}</Text>
                    <LoginFlags u={u} />
                  </Td>
                  <Td color="gray.600">{u.email}</Td>
                  <Td><Badge colorScheme={u.role === "SUPER_ADMIN" ? "purple" : "blue"}>{u.role}</Badge></Td>
                  <Td>{u.team ? <Tag bg={u.team.color || "blue.100"} color="white">{u.team.name}</Tag> : <Tag>—</Tag>}</Td>
                  <Td><Badge colorScheme={u.status === "active" ? "green" : u.status === "suspended" ? "red" : "yellow"}>{u.status}</Badge></Td>
                  <Td color="gray.500" fontSize="xs">{u.lastSeen ? new Date(u.lastSeen).toLocaleString() : "—"}</Td>
                  <Td>
                    <HStack>
                      <IconButton size="sm" aria-label="edit" icon={<EditIcon />} onClick={() => { setEditing(u); edit.onOpen(); }} />
                      <Button size="xs" onClick={() => handleSuspendToggle(u)}>{u.status === "active" ? "Suspend" : "Activate"}</Button>
                      <Menu>
                        <MenuButton as={IconButton} size="sm" icon={<ChevronDownIcon />} aria-label="more" />
                        <MenuList>
                          <MenuItem onClick={() => userAction(u, "force-logout", "Force logout")}>Force logout</MenuItem>
                          <MenuItem onClick={() => userAction(u, "reset-mfa", "Reset MFA")}>Reset MFA</MenuItem>
                          <MenuItem onClick={() => handleResetPassword(u)}>Reset password</MenuItem>
                          <MenuItem onClick={() => { setHealthUser(u); health.onOpen(); }}>Login health</MenuItem>
                        </MenuList>
                      </Menu>
                      <IconButton size="sm" aria-label="delete" colorScheme="red" variant="ghost" icon={<DeleteIcon />} onClick={() => handleDelete(u._id)} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {users.length === 0 && <Tr><Td colSpan={8} textAlign="center" color="gray.500" py={6}>No users</Td></Tr>}
            </Tbody>
          </Table>
        </Box>
      )}

      <UserFormModal open={create.isOpen} onClose={create.onClose} mode="create" teams={teams} allUsers={users} onSaved={load} toast={toast} />
      <UserFormModal open={edit.isOpen} onClose={edit.onClose} mode="edit" user={editing} teams={teams} allUsers={users} onSaved={load} toast={toast} />
      <LoginHealthModal user={healthUser} isOpen={health.isOpen} onClose={health.onClose} />
    </Box>
  );
}

// Small flags under a name: recent wrong passwords (someone struggling to
// sign in; nothing is blocked), or a second account with the same name
// (e.g. the two "Anuj Date" accounts).
function LoginFlags({ u }) {
  const l = u.login || {};
  const same = u.sameNameAs || [];
  if (!l.recentFailures && !same.length) return null;
  return (
    <HStack spacing={1} mt={1} wrap="wrap">
      {l.recentFailures > 0 && (
        <Pill size="sm" tone="warning">{l.recentFailures} wrong {l.recentFailures === 1 ? "password" : "passwords"}</Pill>
      )}
      {same.length > 0 && <Pill size="sm" tone="orange" title={same.join(", ")}>Same name as another account</Pill>}
    </HStack>
  );
}

function UserFormModal({ open, onClose, mode, user, teams, allUsers, onSaved, toast }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (mode === "edit" && user) {
      setF({
        username: user.username || "",
        role: user.role || "TEAM_USER",
        team: user.team?._id || "",
        status: user.status || "active",
        designation: user.designation || "",
        phone: user.phone || "",
        reportingTo: user.reportingTo?._id || user.reportingTo || "",
      });
    } else {
      setF({ username: "", email: "", password: "", role: "TEAM_USER", team: "", designation: "", phone: "", reportingTo: "" });
    }
  }, [mode, user, open]);

  async function save() {
    try {
      if (mode === "create") {
        await api("/admin/users", { method: "POST", body: JSON.stringify(f) });
        toast({ title: "User created", status: "success" });
      } else {
        await api(`/admin/users/${user._id}`, { method: "PATCH", body: JSON.stringify(f) });
        toast({ title: "User updated", status: "success" });
      }
      onClose();
      onSaved();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Modal isOpen={open} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{mode === "create" ? "Add User" : "Edit User"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={3}>
            <FormControl><FormLabel>Username</FormLabel><Input value={f.username || ""} onChange={(e) => setF({ ...f, username: e.target.value })} /></FormControl>
            {mode === "create" && <FormControl><FormLabel>Email</FormLabel><Input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></FormControl>}
            {mode === "create" && <FormControl><FormLabel>Password</FormLabel><Input type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></FormControl>}
            <FormControl><FormLabel>Role</FormLabel>
              <Select value={f.role || "TEAM_USER"} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </Select>
            </FormControl>
            <FormControl><FormLabel>Team</FormLabel>
              <Select placeholder="No team" value={f.team || ""} onChange={(e) => setF({ ...f, team: e.target.value })}>
                {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </Select>
            </FormControl>
            {mode === "edit" && <FormControl><FormLabel>Status</FormLabel>
              <Select value={f.status || "active"} onChange={(e) => setF({ ...f, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </Select>
            </FormControl>}
            <FormControl><FormLabel>Designation</FormLabel><Input value={f.designation || ""} onChange={(e) => setF({ ...f, designation: e.target.value })} /></FormControl>
            <FormControl><FormLabel>Phone</FormLabel><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></FormControl>
            <FormControl gridColumn="1/-1"><FormLabel>Reports to</FormLabel>
              <Select placeholder="No manager" value={f.reportingTo || ""} onChange={(e) => setF({ ...f, reportingTo: e.target.value })}>
                {(allUsers || []).filter((u) => !user || u._id !== user._id).map((u) => (
                  <option key={u._id} value={u._id}>{u.username} ({u.email})</option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={save}>{mode === "create" ? "Create" : "Save"}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function TeamsTab() {
  const toast = useToast();
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const formModal = useDisclosure();
  const [editingTeam, setEditingTeam] = useState(null);
  const empty = { name: "", key: "", color: "#2D5BFF", icon: "users", description: "", lead: "", department: "" };
  const [f, setF] = useState(empty);

  async function load() {
    setLoading(true);
    try {
      const [tRes, dRes, uRes] = await Promise.all([
        api("/admin/teams"),
        api("/departments"),
        api("/admin/users"),
      ]);
      setTeams(tRes.teams || []);
      setDepartments(dRes.departments || []);
      setUsers(uRes.users || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditingTeam(null);
    setF(empty);
    formModal.onOpen();
  }
  function openEdit(t) {
    setEditingTeam(t);
    setF({
      name: t.name || "",
      key: t.key || "",
      color: t.color || "#2D5BFF",
      icon: t.icon || "users",
      description: t.description || "",
      lead: t.lead?._id || "",
      department: t.department?._id || "",
    });
    formModal.onOpen();
  }

  async function save() {
    try {
      const payload = {
        name: f.name,
        color: f.color,
        icon: f.icon,
        description: f.description,
        lead: f.lead || null,
        department: f.department || null,
      };
      if (editingTeam) {
        await api(`/admin/teams/${editingTeam._id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast({ title: "Team updated", status: "success" });
      } else {
        await api("/admin/teams", { method: "POST", body: JSON.stringify({ ...payload, key: f.key || f.name }) });
        toast({ title: "Team created", status: "success" });
      }
      formModal.onClose();
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function deleteTeam(id) {
    if (!window.confirm("Delete this team?")) return;
    try { await api(`/admin/teams/${id}`, { method: "DELETE" }); toast({ title: "Deleted", status: "success" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Box>
      <HStack mb={4} justify="space-between">
        <Heading size="md">Teams</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>Add Team</Button>
      </HStack>
      {loading ? <Spinner /> : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {teams.map((t) => (
            <Box key={t._id} p={4} bg="white" rounded="md" shadow="sm" borderTop="4px solid" borderColor={t.color}>
              <Flex justify="space-between" align="start">
                <Box flex="1">
                  <Heading size="sm">{t.name}</Heading>
                  <HStack mt={1} spacing={2}>
                    <Tag fontSize="xs">{t.key}</Tag>
                    {t.department && <Tag fontSize="xs" bg={t.department.color || "gray.200"} color="white">{t.department.name}</Tag>}
                  </HStack>
                </Box>
                <HStack>
                  <IconButton size="sm" aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(t)} />
                  <IconButton size="sm" aria-label="delete" colorScheme="red" variant="ghost" icon={<DeleteIcon />} onClick={() => deleteTeam(t._id)} />
                </HStack>
              </Flex>
              <Box mt={2} fontSize="sm" color="gray.600">{t.description || "—"}</Box>
              <HStack mt={3} fontSize="xs" color="gray.500" spacing={3}>
                <Text>{t.memberCount || 0} members</Text>
                <Text>•</Text>
                <Text>Lead: {t.lead ? t.lead.username : "unset"}</Text>
              </HStack>
            </Box>
          ))}
          {teams.length === 0 && <Box gridColumn="1/-1" textAlign="center" color="gray.500" py={8}>No teams yet</Box>}
        </SimpleGrid>
      )}

      <Modal isOpen={formModal.isOpen} onClose={formModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingTeam ? "Edit Team" : "Add Team"}</ModalHeader><ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl><FormLabel>Name</FormLabel><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormControl>
              {!editingTeam && <FormControl><FormLabel>Key</FormLabel><Input placeholder="VIDEO" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} /></FormControl>}
              <FormControl><FormLabel>Color</FormLabel><Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Icon</FormLabel><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Department</FormLabel>
                <Select placeholder="None" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })}>
                  {departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Team Lead</FormLabel>
                <Select placeholder="None" value={f.lead} onChange={(e) => setF({ ...f, lead: e.target.value })}>
                  {users.map((u) => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
                </Select>
              </FormControl>
              <FormControl gridColumn="1/-1"><FormLabel>Description</FormLabel><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={formModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={save}>{editingTeam ? "Save" : "Create"}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function DepartmentsTab() {
  const toast = useToast();
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const modal = useDisclosure();
  const [editing, setEditing] = useState(null);
  const empty = { name: "", key: "", color: "#4A5568", description: "", head: "" };
  const [f, setF] = useState(empty);

  async function load() {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([api("/departments"), api("/admin/users")]);
      setDepartments(d.departments || []);
      setUsers(u.users || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setF(empty); modal.onOpen(); }
  function openEdit(d) {
    setEditing(d);
    setF({
      name: d.name, key: d.key, color: d.color || "#4A5568",
      description: d.description || "", head: d.head?._id || "",
    });
    modal.onOpen();
  }

  async function save() {
    try {
      const body = { name: f.name, color: f.color, description: f.description, head: f.head || null };
      if (editing) {
        await api(`/departments/${editing._id}`, { method: "PATCH", body: JSON.stringify(body) });
        toast({ title: "Department updated", status: "success" });
      } else {
        await api("/departments", { method: "POST", body: JSON.stringify({ ...body, key: f.key || f.name }) });
        toast({ title: "Department created", status: "success" });
      }
      modal.onClose();
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this department? Teams will be unassigned.")) return;
    try { await api(`/departments/${id}`, { method: "DELETE" }); toast({ title: "Deleted", status: "success" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Box>
      <HStack mb={4} justify="space-between">
        <Heading size="md">Departments</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>Add Department</Button>
      </HStack>
      {loading ? <Spinner /> : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {departments.map((d) => (
            <Box key={d._id} p={4} bg="white" rounded="md" shadow="sm" borderTop="4px solid" borderColor={d.color}>
              <Flex justify="space-between" align="start">
                <Box flex="1">
                  <Heading size="sm">{d.name}</Heading>
                  <Tag mt={1} fontSize="xs">{d.key}</Tag>
                </Box>
                <HStack>
                  <IconButton size="sm" aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(d)} />
                  <IconButton size="sm" aria-label="delete" colorScheme="red" variant="ghost" icon={<DeleteIcon />} onClick={() => remove(d._id)} />
                </HStack>
              </Flex>
              <Box mt={2} fontSize="sm" color="gray.600">{d.description || "—"}</Box>
              <HStack mt={3} fontSize="xs" color="gray.500" spacing={3}>
                <Text>{d.teamCount || 0} teams</Text>
                <Text>•</Text>
                <Text>{d.memberCount || 0} members</Text>
                <Text>•</Text>
                <Text>Head: {d.head ? d.head.username : "unset"}</Text>
              </HStack>
            </Box>
          ))}
          {departments.length === 0 && <Box gridColumn="1/-1" textAlign="center" color="gray.500" py={8}>No departments yet</Box>}
        </SimpleGrid>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit Department" : "Add Department"}</ModalHeader><ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl><FormLabel>Name</FormLabel><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormControl>
              {!editing && <FormControl><FormLabel>Key</FormLabel><Input placeholder="CREATIVE" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} /></FormControl>}
              <FormControl><FormLabel>Color</FormLabel><Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Department Head</FormLabel>
                <Select placeholder="None" value={f.head} onChange={(e) => setF({ ...f, head: e.target.value })}>
                  {users.map((u) => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
                </Select>
              </FormControl>
              <FormControl gridColumn="1/-1"><FormLabel>Description</FormLabel><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={save}>{editing ? "Save" : "Create"}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function OrgChartTab() {
  const toast = useToast();
  const [data, setData] = useState(null);
  useEffect(() => {
    api("/admin/orgchart").then(setData).catch((e) => toast({ title: e.message, status: "error" }));
    // eslint-disable-next-line
  }, []);

  if (!data) return <Spinner />;
  const { byManager } = data;

  function Node({ user, depth = 0 }) {
    const reports = byManager[String(user._id)] || [];
    return (
      <Box ml={depth * 6} mt={2} pl={depth ? 4 : 0} borderLeft={depth ? "2px solid" : "none"} borderColor="gray.200">
        <HStack p={2} bg="white" rounded="md" shadow="xs" spacing={3} _hover={{ bg: "gray.50" }}>
          <Box w={2} h={2} bg={user.team?.color || "gray.300"} rounded="full" />
          <Box>
            <Text fontWeight="600">{user.username}</Text>
            <Text fontSize="xs" color="gray.500">{user.designation || "—"} · {user.email}</Text>
          </Box>
          <Box flex="1" />
          <Badge colorScheme={user.role === "SUPER_ADMIN" ? "purple" : "blue"} fontSize="2xs">{user.role}</Badge>
          {user.team && <Tag size="sm" bg={user.team.color || "gray.200"} color="white">{user.team.name}</Tag>}
        </HStack>
        {reports.map((r) => <Node key={r._id} user={r} depth={depth + 1} />)}
      </Box>
    );
  }

  const roots = byManager["ROOT"] || [];
  return (
    <Box>
      <Heading size="md" mb={4}>Reporting hierarchy</Heading>
      {roots.length === 0 ? <Text color="gray.500">No users yet</Text> : roots.map((u) => <Node key={u._id} user={u} />)}
    </Box>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const toast = useToast();
  useEffect(() => {
    api("/admin/audit").then((d) => setLogs(d.logs || [])).catch((e) => toast({ title: e.message, status: "error" }));
    // eslint-disable-next-line
  }, []);
  return (
    <Box bg="white" rounded="md" shadow="sm" overflow="auto">
      <Table size="sm">
        <Thead bg="gray.50"><Tr><Th>When</Th><Th>Actor</Th><Th>Action</Th><Th>Target</Th><Th>Metadata</Th></Tr></Thead>
        <Tbody>
          {logs.map((l) => (
            <Tr key={l._id}>
              <Td fontSize="xs">{new Date(l.createdAt).toLocaleString()}</Td>
              <Td>{l.actorEmail || "—"}</Td>
              <Td><Badge>{l.action}</Badge></Td>
              <Td fontSize="xs">{l.target}{l.targetId ? `:${String(l.targetId).slice(-6)}` : ""}</Td>
              <Td fontSize="xs" color="gray.500">{Object.keys(l.metadata || {}).length ? JSON.stringify(l.metadata) : "—"}</Td>
            </Tr>
          ))}
          {logs.length === 0 && <Tr><Td colSpan={5} textAlign="center" color="gray.500" py={6}>No activity yet</Td></Tr>}
        </Tbody>
      </Table>
    </Box>
  );
}

function InvitesTab() {
  const toast = useToast();
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const create = useDisclosure();
  const [f, setF] = useState({ email: "", role: "TEAM_USER", team: "", designation: "", expiresInHours: 72 });
  const [lastLink, setLastLink] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [i, t] = await Promise.all([api("/admin/invites"), api("/admin/teams")]);
      setInvites(i.invites || []);
      setTeams(t.teams || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function createInvite() {
    try {
      const body = {
        email: f.email,
        role: f.role,
        team: f.team || null,
        designation: f.designation,
        expiresInHours: Number(f.expiresInHours) || 72,
      };
      const r = await api("/admin/invites", { method: "POST", body: JSON.stringify(body) });
      setLastLink(r.acceptUrl);
      toast({ title: "Invite created — copy the link below", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function revoke(id) {
    if (!window.confirm("Revoke this invite?")) return;
    try { await api(`/admin/invites/${id}`, { method: "DELETE" }); toast({ title: "Revoked", status: "success" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  function copyLink(link) {
    navigator.clipboard.writeText(link);
    toast({ title: "Link copied", status: "success", duration: 1500 });
  }

  function inviteLink(token) {
    return `${window.location.origin}/invite/${token}`;
  }

  function statusOf(inv) {
    if (inv.revokedAt) return { label: "revoked", color: "gray" };
    if (inv.usedAt) return { label: "used", color: "green" };
    if (new Date(inv.expiresAt) < new Date()) return { label: "expired", color: "red" };
    return { label: "pending", color: "yellow" };
  }

  return (
    <Box>
      <HStack mb={4} justify="space-between">
        <Heading size="md">Invites</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => { setLastLink(null); create.onOpen(); }}>New Invite</Button>
      </HStack>
      {loading ? <Spinner /> : (
        <Box bg="white" rounded="md" shadow="sm" overflow="auto">
          <Table size="sm">
            <Thead bg="gray.50"><Tr><Th>Email</Th><Th>Role</Th><Th>Team</Th><Th>Status</Th><Th>Expires</Th><Th>Created by</Th><Th></Th></Tr></Thead>
            <Tbody>
              {invites.map((inv) => {
                const s = statusOf(inv);
                return (
                  <Tr key={inv._id}>
                    <Td>{inv.email}</Td>
                    <Td><Badge colorScheme={inv.role === "SUPER_ADMIN" ? "purple" : "blue"}>{inv.role}</Badge></Td>
                    <Td>{inv.team ? <Tag bg={inv.team.color || "blue.500"} color="white">{inv.team.name}</Tag> : "—"}</Td>
                    <Td><Badge colorScheme={s.color}>{s.label}</Badge></Td>
                    <Td fontSize="xs" color="gray.500">{new Date(inv.expiresAt).toLocaleString()}</Td>
                    <Td fontSize="xs">{inv.createdBy?.email || "—"}</Td>
                    <Td>
                      <HStack>
                        {s.label === "pending" && (
                          <>
                            <IconButton size="sm" icon={<CopyIcon />} aria-label="copy" onClick={() => copyLink(inviteLink(inv.token))} />
                            <IconButton size="sm" colorScheme="red" variant="ghost" icon={<DeleteIcon />} aria-label="revoke" onClick={() => revoke(inv._id)} />
                          </>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
              {invites.length === 0 && <Tr><Td colSpan={7} textAlign="center" color="gray.500" py={6}>No invites</Td></Tr>}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={create.isOpen} onClose={create.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Invite</ModalHeader><ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl gridColumn="1/-1"><FormLabel>Email</FormLabel><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Role</FormLabel>
                <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                  <option>TEAM_USER</option><option>SUPER_ADMIN</option>
                </Select>
              </FormControl>
              <FormControl><FormLabel>Team</FormLabel>
                <Select placeholder="No team" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })}>
                  {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Designation</FormLabel><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Expires in (hours)</FormLabel>
                <NumberInput value={f.expiresInHours} min={1} max={720}>
                  <NumberInputField onChange={(e) => setF({ ...f, expiresInHours: e.target.value })} />
                </NumberInput>
              </FormControl>
            </SimpleGrid>
            {lastLink && (
              <Box mt={4} p={3} bg="green.50" rounded="md">
                <Text fontWeight="600" mb={2}>Invite link (share this with the user):</Text>
                <HStack>
                  <Code flex="1" p={2} fontSize="xs" overflowX="auto">{lastLink}</Code>
                  <Button size="sm" leftIcon={<CopyIcon />} onClick={() => copyLink(lastLink)}>Copy</Button>
                </HStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={create.onClose}>Close</Button>
            <Button colorScheme="blue" onClick={createInvite}>Create invite</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function StorageTab() {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credsForm, setCredsForm] = useState({ clientId: "", clientSecret: "", redirectUrl: "" });
  const [showCredsForm, setShowCredsForm] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const s = await api("/admin/storage/status");
      setStatus(s);
      if (s?.providers?.gdrive?.available) setShowCredsForm(false);
      setCredsForm((f) => ({ ...f, redirectUrl: f.redirectUrl || `${window.location.origin}/auth/google-drive/callback` }));
    }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function saveCreds() {
    if (!credsForm.clientId || !credsForm.clientSecret || !credsForm.redirectUrl) {
      toast({ title: "All three fields are required", status: "warning" });
      return;
    }
    setSavingCreds(true);
    try {
      await api("/admin/storage/google-drive/credentials", { method: "POST", body: JSON.stringify(credsForm) });
      toast({ title: "Credentials saved. Click Connect to authorize.", status: "success" });
      await load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setSavingCreds(false); }
  }

  async function clearCreds() {
    if (!window.confirm("Clear Google Drive credentials? This will also disconnect any active OAuth session.")) return;
    try {
      await api("/admin/storage/google-drive/credentials", { method: "DELETE" });
      toast({ title: "Credentials cleared", status: "info" });
      await load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function connect() {
    try {
      const r = await api("/admin/storage/google-drive/connect", { method: "POST" });
      const w = window.open(r.url, "_blank", "width=520,height=640");
      const interval = setInterval(async () => {
        if (w && w.closed) {
          clearInterval(interval);
          await load();
          toast({ title: "Refreshed status", status: "info", duration: 1500 });
        }
      }, 1000);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Google Drive? Existing files in Drive remain there. New uploads will go to local storage until reconnected.")) return;
    try {
      await api("/admin/storage/google-drive/disconnect", { method: "POST" });
      toast({ title: "Disconnected", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  if (loading) return <Spinner />;
  const drv = status?.providers?.gdrive;
  const lcl = status?.providers?.local;
  return (
    <Box>
      <Box mb={4} p={4} bg="white" rounded="md" shadow="sm">
        <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>Active provider</Text>
        <Text fontSize="lg" fontWeight={700}>{status?.activeProvider === "gdrive" ? "Google Drive" : "Local disk"}</Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Box p={5} bg="white" rounded="md" shadow="sm" borderTop="3px solid" borderColor="green.400">
          <Heading size="sm" mb={2}>Local disk</Heading>
          <Text fontSize="sm" color="gray.600" mb={3}>{lcl?.info || ""}</Text>
          <Badge colorScheme="green">Always available</Badge>
        </Box>

        <Box p={5} bg="white" rounded="md" shadow="sm" borderTop="3px solid" borderColor={drv?.connected ? "blue.400" : "gray.300"}>
          <HStack justify="space-between" mb={2}>
            <Heading size="sm">Google Drive</Heading>
            {drv?.connected
              ? <Badge colorScheme="blue">Connected</Badge>
              : drv?.available
                ? <Badge colorScheme="yellow">Not connected</Badge>
                : <Badge>Env not configured</Badge>}
          </HStack>
          <Text fontSize="sm" color="gray.600" mb={3}>{drv?.info || ""}</Text>
          {!drv?.available && !showCredsForm && (
            <Box bg="gray.50" p={3} rounded="md" fontSize="xs" color="gray.600">
              <Text fontWeight={600} mb={1}>To enable Drive (choose one):</Text>
              <Text mt={2} fontWeight={600}>Option A — Configure via this portal (recommended):</Text>
              <Text>1. Create OAuth client at <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" style={{color:"var(--accent-text)",textDecoration:"underline"}}>Google Cloud Console</a> (Web application)</Text>
              <Text>2. Authorized redirect URI: <Code fontSize="xs">{window.location.origin}/auth/google-drive/callback</Code></Text>
              <Button mt={2} colorScheme="blue" size="sm" onClick={() => setShowCredsForm(true)}>Enter credentials below →</Button>
              <Text mt={3} fontWeight={600}>Option B — via Railway env vars:</Text>
              <Text>Set <Code fontSize="xs">GOOGLE_CLIENT_ID</Code>, <Code fontSize="xs">GOOGLE_CLIENT_SECRET</Code>, <Code fontSize="xs">GOOGLE_DRIVE_REDIRECT_URL</Code>, redeploy, refresh this page.</Text>
            </Box>
          )}
          {!drv?.available && showCredsForm && (
            <Box bg="gray.50" p={3} rounded="md" fontSize="sm">
              <Text fontWeight={600} mb={2}>Paste your Google OAuth credentials</Text>
              <Text fontSize="xs" color="gray.500" mb={2}>Stored encrypted-at-rest in our DB. Used only to start the Drive OAuth handshake.</Text>
              <Input
                size="sm" mb={2}
                placeholder="GOOGLE_CLIENT_ID (looks like 1234-abc.apps.googleusercontent.com)"
                value={credsForm.clientId}
                onChange={(e) => setCredsForm((f) => ({ ...f, clientId: e.target.value.trim() }))}
              />
              <Input
                size="sm" mb={2} type="password"
                placeholder="GOOGLE_CLIENT_SECRET"
                value={credsForm.clientSecret}
                onChange={(e) => setCredsForm((f) => ({ ...f, clientSecret: e.target.value.trim() }))}
              />
              <Input
                size="sm" mb={2}
                placeholder="Redirect URL"
                value={credsForm.redirectUrl}
                onChange={(e) => setCredsForm((f) => ({ ...f, redirectUrl: e.target.value.trim() }))}
              />
              <HStack>
                <Button colorScheme="blue" size="sm" isLoading={savingCreds} onClick={saveCreds}>Save credentials</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowCredsForm(false)}>Cancel</Button>
              </HStack>
            </Box>
          )}
          {drv?.available && (
            <HStack mt={3}>
              {drv.connected
                ? <Button colorScheme="red" variant="outline" size="sm" onClick={disconnect}>Disconnect</Button>
                : <Button colorScheme="blue" size="sm" onClick={connect}>Connect Google Drive</Button>}
              <Button size="sm" variant="ghost" onClick={load}>Refresh</Button>
              {drv.credSource === "db" && (
                <Button size="sm" variant="ghost" colorScheme="red" onClick={clearCreds}>Clear stored creds</Button>
              )}
            </HStack>
          )}
          {drv?.credSource && (
            <Text mt={2} fontSize="[10px]" color="gray.400">Credentials source: {drv.credSource === "env" ? "Railway env vars" : "Portal (stored in DB)"}</Text>
          )}
        </Box>
      </SimpleGrid>

      <Box mt={4} p={4} bg="yellow.50" rounded="md" fontSize="sm" color="yellow.900">
        <Text fontWeight={600} mb={1}>⚠️ Drive quota note</Text>
        <Text>Google Drive files count against the connected account's quota. Free Google = 15 GB shared with Gmail/Photos. Workspace Business Standard = 2 TB pooled.</Text>
      </Box>
    </Box>
  );
}

function PerformanceTab() {
  const toast = useToast();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const r = await api("/admin/efficiency/team");
      setScores(r.scores || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function recomputeAll() {
    setRecomputing(true);
    try {
      const r = await api("/admin/efficiency/recompute-all", { method: "POST" });
      toast({ title: `Recomputed ${r.updated} users`, status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setRecomputing(false); }
  }

  const elite = scores.filter((s) => s.band === "elite");
  const overdueOpts = scores.filter((s) => s.dataPoints?.overdueActive >= 3);
  const overloaded = scores.filter((s) => s.dataPoints && s.dataPoints.deptAvgActive > 0 && s.dataPoints.activeTasks / s.dataPoints.deptAvgActive >= 2);

  const bandColor = {
    elite: "purple", strong: "blue", average: "yellow", needs_attention: "red", insufficient_data: "gray",
  };

  if (loading) return <Spinner />;
  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">Team performance</Heading>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={recomputeAll} isLoading={recomputing}>
          Recompute all
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <Box bg="white" p={4} rounded="md" shadow="sm" borderTop="3px solid" borderColor="purple.400">
          <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">Elite performers</Text>
          <Heading size="lg">{elite.length}</Heading>
          <Text fontSize="xs" color="gray.500" mt={1}>Score 850+</Text>
        </Box>
        <Box bg="white" p={4} rounded="md" shadow="sm" borderTop="3px solid" borderColor="red.400">
          <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">Has 3+ overdue</Text>
          <Heading size="lg" color="red.500">{overdueOpts.length}</Heading>
          <Text fontSize="xs" color="gray.500" mt={1}>Active tasks past deadline</Text>
        </Box>
        <Box bg="white" p={4} rounded="md" shadow="sm" borderTop="3px solid" borderColor="orange.400">
          <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase">Possibly overloaded</Text>
          <Heading size="lg" color="orange.500">{overloaded.length}</Heading>
          <Text fontSize="xs" color="gray.500" mt={1}>Active load 2× dept avg</Text>
        </Box>
      </SimpleGrid>

      <Box bg="white" rounded="md" shadow="sm" overflow="auto">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>User</Th><Th>Team</Th><Th>Score</Th><Th>Band</Th>
              <Th>Tasks</Th><Th>On-time</Th><Th>Overdue</Th><Th>Apprs</Th><Th>Updated</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scores.map((s) => {
              const dp = s.dataPoints || {};
              return (
                <Tr key={s._id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => s.user && navigate(`/users/${s.user._id}`)}>
                  <Td>
                    <HStack>
                      <Box w="6px" h="6px" rounded="full" bg={s.user?.team?.color || "gray.300"} />
                      <Text fontWeight={600}>{s.user?.username || "—"}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{s.user?.designation || s.user?.email}</Text>
                  </Td>
                  <Td>{s.user?.team?.name || "—"}</Td>
                  <Td><Text fontWeight={700} fontFamily="mono">{s.insufficient ? "—" : s.score}</Text></Td>
                  <Td><Badge colorScheme={bandColor[s.band]}>{s.band.replace(/_/g, " ")}</Badge></Td>
                  <Td fontSize="xs" color="gray.600">{dp.completed || 0}/{dp.assigned || 0}</Td>
                  <Td fontSize="xs" color="gray.600">{dp.onTimeOrEarly || 0}</Td>
                  <Td fontSize="xs" color={(dp.overdueActive || 0) > 0 ? "red.500" : "gray.600"}>{dp.overdueActive || 0}</Td>
                  <Td fontSize="xs" color="gray.600">{dp.appreciations || 0}</Td>
                  <Td fontSize="2xs" color="gray.400">{s.computedAt ? new Date(s.computedAt).toLocaleString() : "—"}</Td>
                </Tr>
              );
            })}
            {scores.length === 0 && <Tr><Td colSpan={9} textAlign="center" color="gray.500" py={6}>No scores yet — click 'Recompute all'.</Td></Tr>}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const role = useMemo(() => localStorage.getItem("role"), []);
  useShellHeader({ title: "Admin Console" }, []);
  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
    else if (role !== "SUPER_ADMIN") navigate("/");
  }, [role, navigate]);
  if (role !== "SUPER_ADMIN") return null;

  return (
    <Box>
      <StatsBar />
      <Tabs bg="white" rounded="md" shadow="sm">
        <TabList>
          <Tab>Users</Tab>
          <Tab>Invites</Tab>
          <Tab>Teams</Tab>
          <Tab>Departments</Tab>
          <Tab>Org Chart</Tab>
          <Tab>Storage</Tab>
          <Tab>Performance</Tab>
          <Tab>Activity Log</Tab>
        </TabList>
        <TabPanels>
          <TabPanel><UsersTab /></TabPanel>
          <TabPanel><InvitesTab /></TabPanel>
          <TabPanel><TeamsTab /></TabPanel>
          <TabPanel><DepartmentsTab /></TabPanel>
          <TabPanel><OrgChartTab /></TabPanel>
          <TabPanel><StorageTab /></TabPanel>
          <TabPanel><PerformanceTab /></TabPanel>
          <TabPanel><AuditTab /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
